import Link from 'next/link'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import React, { useCallback, useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css, ThemeContext } from 'styled-components'

import CorporateJoinUs from '../../components/CorporateJoinUs'
import Flex from '../../components/Layout/Flex'
import { Col, Row } from '../../components/Layout/FlexGrid'
import MaxWidth from '../../components/Layout/MaxWidth'
import NextSeo from '../../components/NextSeo'
import Picture from '../../components/Picture'
import {
  CorporatePartnerCorpSkeleton,
  CorporateSignedCorpSkeleton
} from '../../components/Skeleton'
import SvgIcon from '../../components/SvgIcon'
import { MEDIA_CONDITIONS } from '../../constants'
import { AppviewNavigation } from '../../containers/Appview'
import Button from '../../containers/Button'
import CorporateHomeArticle from '../../containers/Corporate/CorporateHomeArticle'
import { useTracking } from '../../hooks'
import { useTranslation } from '../../i18n'
import BannerSvg from '../../public/static/images/corporate/corporate-landing-banner.svg'
import iconMethodSvgActivities from '../../public/static/images/corporate/icon-corporate-cooperation-method-activities.svg'
import iconMethodSvgCoorganize from '../../public/static/images/corporate/icon-corporate-cooperation-method-coorganize.svg'
import iconMethodSvgCsr from '../../public/static/images/corporate/icon-corporate-cooperation-method-csr.svg'
import iconMethodSvgDonation from '../../public/static/images/corporate/icon-corporate-cooperation-method-donation.svg'
import iconMethodSvgEmployee from '../../public/static/images/corporate/icon-corporate-cooperation-method-employee.svg'
import iconMethodSvgMatching from '../../public/static/images/corporate/icon-corporate-cooperation-method-matching.svg'
import iconMethodSvgSponsor from '../../public/static/images/corporate/icon-corporate-cooperation-method-sponsor.svg'
import iconRewardSvgApps from '../../public/static/images/corporate/icon-corporate-reward-apps.svg'
import iconRewardSvgCeremony from '../../public/static/images/corporate/icon-corporate-reward-ceremony.svg'
import iconRewardSvgGuest from '../../public/static/images/corporate/icon-corporate-reward-guest.svg'
import iconRewardSvgInterview from '../../public/static/images/corporate/icon-corporate-reward-interview.svg'
import iconRewardSvgTrophy from '../../public/static/images/corporate/icon-corporate-reward-trophy.svg'
import iconRewardSvgVideo from '../../public/static/images/corporate/icon-corporate-reward-video.svg'
import { getCorporateHomePageRequest } from '../../redux/corporate/actions'
import {
  getCorporateHomeArticleIds,
  getCorporateHomeCorporateCategory,
  getCorporateHomeCorporates
} from '../../redux/corporate/selectors'
import { clearRedux } from '../../redux/master/actions'
import { getPageNameFromCurrentPath } from '../../utils/commonUtils'
import { getColor, getFontSize, objectFit } from '../../utils/cssUtils'
import {
  corporateClickContact,
  corporateHomeClickDetail,
  corporateHomeClickListing,
  corporateHomeClickMoreArticle,
  corporateHomeView
} from '../../utils/dataTracker'
import mq from '../../utils/mediaQuery'
import { isExist } from '../../utils/ramdaUtils'

const FlexCenter = props => <Flex align='center' justify='center' {...props} />
const Section = styled.section`
  padding: 40px 0;
`
const MaxWidthSection = ({ children, ...props }) => (
  <Section {...props}>
    <MaxWidth>{children}</MaxWidth>
  </Section>
)
const KeyVisual = styled(BannerSvg)`
  width: 100%;
`
const StyledPicture = styled(Picture)`
  position: relative;
  width: 100%;
  height: 100%;
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    ${objectFit('contain')}
  }
`
const Wrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  background-color: #fff;
  font-size: ${getFontSize('body2')};
`
const SectionTitle = styled.h3`
  font-size: ${getFontSize('headline')};
  color: ${getColor('black')};
  font-weight: 600;
  text-align: center;
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background-color: ${getColor('black')};
    margin: 8px auto 0;
  }
`
const MainImgContainer = styled.div`
  width: 100%;
`
const BtnWrapper = styled(FlexCenter)`
  margin-top: 24px;
`
const MoreBtn = styled(props => <Button fullWidth height={48} {...props} />)`
  max-width: 180px;
`
const IntroHeadline = styled.h1`
  color: ${getColor('black')};
  font-size: ${getFontSize('display2')};
  text-align: center;
  font-weight: 600;
  ${mq.mobile} {
    font-size: ${getFontSize('display1')};
  }
`
const IntroSubHeadline = styled.h3`
  color: ${getColor('black')};
  font-size: ${getFontSize('title')};
  margin-top: 24px;
  font-weight: 600;
  text-align: center;
  ${mq.mobile} {
    padding: 0 50px;
  }
`
const IntroContent = styled.p`
  color: ${getColor('grey')};
  margin-top: 8px;
  padding: 0 204px;
  ${mq.tablet} {
    padding: 0 55px;
  }
  ${mq.mobile} {
    padding: 0;
  }
`
const Articles = styled(Row)`
  margin-top: 24px;
`
const ArticleItemWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  ${mq.tabletMobile} {
    > a {
      padding-bottom: 24px;
    }
  }
`
const Methods = styled(Row)`
  display: inline-flex;
  justify-content: center;
  ${({ gutter }) =>
    gutter &&
    css`
      ${mq.desktop} {
        width: ${160 * 4 + 4 * gutter[0].xl}px;
      }
      ${mq.tablet} {
        width: ${160 * 2 + 2 * gutter[0].md}px;
      }
      ${mq.mobile} {
        width: ${140 * 2 + 2 * gutter[0].xs}px;
      }
    `}
`
const MethodWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  ${mq.desktop} {
    ${({ index }) =>
      index < 3
        ? css`
            width: ${100 / 4}%;
            padding: 0 73px 28.5px 73px;
            &:nth-of-type(1) {
              margin-left: ${80 + 73}px;
            }
            &:nth-of-type(3) {
              margin-right: ${80 + 73}px;
            }
          `
        : css`
            width: ${100 / 4}%;
            padding: 28.5px 73px 0px 73px;
          `}
  }
  ${mq.tablet} {
    width: 50%;
    ${({ index }) =>
      index < 2
        ? css`
            padding: 0 85.5px 20px 85.5px;
          `
        : index === 6
        ? css`
            padding: 20px 85.5px 0 85.5px;
          `
        : css`
            padding: 20px 85.5px;
          `}
  }
  ${mq.mobile} {
    width: 50%;
    ${({ index }) =>
      index < 2
        ? css`
            padding: 0 12px 16px 12px;
          `
        : index === 6
        ? css`
            padding: 16px 12px 0 12px;
          `
        : css`
            padding: 16px 12px;
          `}
  }
`
const Method = styled(FlexCenter)`
  color: ${getColor('theme')};
  width: 160px;
  height: 160px;
  ${mq.mobile} {
    width: 140px;
    height: 140px;
  }
`
const MethodDesc = styled.span`
  margin-top: 8px;
  text-align: center;
  display: flex;
  justify-content: center;
`
const RewardTypes = styled(Row)`
  display: inline-flex;
  ${({ gutter }) =>
    gutter &&
    css`
      ${mq.desktop} {
        width: ${140 * 6 + 6 * gutter[0].xl}px;
      }
      ${mq.tablet} {
        width: ${140 * 3 + 3 * gutter[0].md}px;
      }
      ${mq.mobile} {
        width: ${120 * 2 + 2 * gutter[0].xs}px;
      }
    `}
`
const Reward = styled(FlexCenter)`
  color: ${getColor('theme')};
  width: 140px;
  height: 140px;
  ${mq.mobile} {
    width: 120px;
    height: 120px;
  }
`
const RewardTypeDesc = styled.span`
  margin-top: 8px;
  text-align: center;
  display: flex;
  justify-content: center;
`
const Partners = styled(Row)`
  display: inline-flex;
  justify-content: center;
  ${({ gutter }) =>
    gutter &&
    css`
      ${mq.desktop} {
        width: ${282 * 5 + 5 * gutter[0].xl}px;
      }
      ${mq.tablet} {
        width: ${197 * 3 + 3 * gutter[0].md}px;
      }
      ${mq.mobile} {
        width: ${171 * 2 + 2 * gutter[0].xs}px;
      }
    `}
`
const PartnerCorp = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
`
const PartnerCorpLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  img {
    object-fit: contain;
    ${objectFit('contain')}
  }
  width: 282px;
  height: 172px;
  padding: 30px 0;
  ${mq.tablet} {
    width: 197px;
    height: 154px;
    padding: 24px 0;
  }
  ${mq.mobile} {
    width: 171px;
    height: 134px;
    padding: 16px 0;
  }
  &:hover {
    background-color: rgba(181, 181, 181, 0.2);
  }
`
const SignedCorps = styled(Row)`
  display: inline-flex;
  justify-content: center;
  ${({ gutter }) =>
    gutter &&
    css`
      ${mq.desktop} {
        width: ${112 * 8 + 8 * gutter.xl}px;
      }
      ${mq.tablet} {
        width: ${112 * 6 + 6 * gutter.md}px;
      }
      ${mq.mobile} {
        width: ${72 * 4 + 4 * gutter.xs}px;
      }
    `}
`
const SignedCorp = styled(FlexCenter)`
  width: 112px;
  height: 112px;
  ${mq.mobile} {
    width: 72px;
    height: 72px;
  }
`

const methods = [
  { i18nKey: 'corporate.methods.employee', icon: iconMethodSvgEmployee },
  { i18nKey: 'corporate.methods.matching', icon: iconMethodSvgMatching },
  { i18nKey: 'corporate.methods.donation', icon: iconMethodSvgDonation },
  { i18nKey: 'corporate.methods.coorganize', icon: iconMethodSvgCoorganize },
  { i18nKey: 'corporate.methods.csr', icon: iconMethodSvgCsr },
  { i18nKey: 'corporate.methods.activities', icon: iconMethodSvgActivities },
  { i18nKey: 'corporate.methods.sponsor', icon: iconMethodSvgSponsor }
]

const rewards = [
  { i18nKey: 'corporate.rewards.apps', icon: iconRewardSvgApps },
  { i18nKey: 'corporate.rewards.ceremony', icon: iconRewardSvgCeremony },
  { i18nKey: 'corporate.rewards.guest', icon: iconRewardSvgGuest },
  { i18nKey: 'corporate.rewards.interview', icon: iconRewardSvgInterview },
  { i18nKey: 'corporate.rewards.trophy', icon: iconRewardSvgTrophy },
  { i18nKey: 'corporate.rewards.video', icon: iconRewardSvgVideo }
]

const Corporate = props => {
  const { t } = useTranslation('common')

  const { color } = useContext(ThemeContext)

  const router = useRouter()

  const track = useTracking()

  const dispatch = useDispatch()

  const pageName = getPageNameFromCurrentPath(router.pathname)

  const articleIds = useSelector(getCorporateHomeArticleIds)

  const corporateCategory = useSelector(getCorporateHomeCorporateCategory)

  const corporates = useSelector(getCorporateHomeCorporates)

  const renderMethods = useCallback(
    ({ i18nKey, icon: Icon }, i) => (
      <MethodWrapper index={i} key={i}>
        <Method>
          <SvgIcon svg={<Icon />} />
        </Method>
        <MethodDesc>{t(i18nKey)}</MethodDesc>
      </MethodWrapper>
    ),
    []
  )

  const renderRewards = useCallback(
    ({ i18nKey, icon: Icon }, i) => (
      <Col xl={4} md={8} xs={12} key={i}>
        <Reward>
          <SvgIcon svg={<Icon />} />
        </Reward>
        <RewardTypeDesc>{t(i18nKey)}</RewardTypeDesc>
      </Col>
    ),
    []
  )

  const renderPartnerCorps = useCallback((corp, i) => {
    const corporateNo = R.propOr(i, 'corporateNo')(corp)
    return (
      <PartnerCorp xl={6} md={8} xs={12} key={corporateNo || i}>
        {R.isNil(corp) ? (
          <CorporatePartnerCorpSkeleton />
        ) : (
          <Link href={`/corporate/${corporateNo}`} passHref>
            <PartnerCorpLink
              onClick={e => track(corporateHomeClickDetail(corporateNo))}
            >
              <StyledPicture
                showPlaceholder
                placeholderDimension={[112, 112]}
                image={R.pathOr('', ['logoImageFile', 'url'], corp)}
                sizes='172px'
              />
            </PartnerCorpLink>
          </Link>
        )}
      </PartnerCorp>
    )
  }, [])

  const renderSignedCorps = useCallback((corp, i) => {
    const corporateNo = R.propOr(i, 'corporateNo')(corp)
    return (
      <Col xl={3} md={4} xs={6} key={corporateNo}>
        {R.isNil(corp) ? (
          <CorporateSignedCorpSkeleton />
        ) : (
          <SignedCorp>
            <StyledPicture
              showPlaceholder
              image={R.pathOr('', ['logoImageFile', 'url'], corp)}
              sizes={`${MEDIA_CONDITIONS.DESKTOP_TABLET} 112px, 72px`}
            />
          </SignedCorp>
        )}
      </Col>
    )
  }, [])

  const handleMoreArticlesClick = useCallback(e => {
    track(corporateHomeClickMoreArticle())
  }, [])

  const handleGoToListingBtnClick = useCallback(e => {
    track(corporateHomeClickListing())
  })

  const joinUsLinkClickCallback = useCallback(
    position => e => {
      track(corporateClickContact(pageName, position))
    },
    []
  )

  useEffect(() => {
    track(corporateHomeView(), true)
    return () => {
      dispatch(clearRedux(['articleState', 'result']))
    }
  }, [])

  return (
    <Wrapper>
      <NextSeo
        title={t('seo.corporate.home.ogTitle')}
        description={t('seo.corporate.home.ogDescription')}
        image='/static/images/og-corporate-home.png'
      />
      <MaxWidthSection>
        <IntroHeadline>{t('corporate.home.headline')}</IntroHeadline>
        <MainImgContainer>
          <KeyVisual />
        </MainImgContainer>
        <IntroSubHeadline>{t('corporate.home.subHeadline')}</IntroSubHeadline>
        <IntroContent>{t('corporate.home.intro')}</IntroContent>
        <BtnWrapper>
          <Link href='/corporate/contact' as='/corporate/contact'>
            <a onClick={joinUsLinkClickCallback('page_top')}>
              <MoreBtn
                css={`
                  max-width: none;
                  width: auto;
                `}
              >
                {t('corporate.becomeCorporatePartner')}
              </MoreBtn>
            </a>
          </Link>
        </BtnWrapper>
      </MaxWidthSection>
      <MaxWidthSection
        css={`
          background-color: rgba(181, 181, 181, 0.2);
        `}
      >
        <SectionTitle>{t('corporate.home.articleTitle')}</SectionTitle>
        <Articles gutter={24}>
          <Col xl={12} md={24} xs={24}>
            <CorporateHomeArticle
              articleId={isExist(articleIds) ? R.head(articleIds) : null}
            />
          </Col>
          <Col xl={12} md={24} xs={24}>
            <ArticleItemWrapper>
              {R.addIndex(R.map)(
                (articleId, i) => (
                  <CorporateHomeArticle
                    articleId={articleId}
                    listView
                    key={articleId || i}
                  />
                ),
                isExist(articleIds) ? R.tail(articleIds) : Array(3).fill(null)
              )}
            </ArticleItemWrapper>
          </Col>
        </Articles>
        <BtnWrapper>
          <Link
            href={`/article?tagId=${process.env.ARTICLE_TAG_ID_CORPORATE}`}
            as='/article?tag=corporate'
          >
            <a onClick={handleMoreArticlesClick}>
              <MoreBtn>{t('corporate.home.moreArticles')}</MoreBtn>
            </a>
          </Link>
        </BtnWrapper>
      </MaxWidthSection>
      <MaxWidthSection>
        <SectionTitle>{t('corporate.home.cooperationMethods')}</SectionTitle>
        <FlexCenter
          css={`
            border: 1px solid #f3d6d9;
            border-radius: 4px;
            padding: 24px 0;
            margin-top: 24px;
          `}
        >
          <Methods
            gutter={[
              { xl: 146, md: 171, xs: 24 },
              { xl: 57, md: 40, xs: 32 }
            ]}
          >
            {R.addIndex(R.map)(renderMethods, methods)}
          </Methods>
        </FlexCenter>
      </MaxWidthSection>
      <Section>
        <SectionTitle>{t('corporate.home.rewards')}</SectionTitle>
        <FlexCenter
          css={`
            margin: 32px 0 40px 0;
          `}
        >
          <RewardTypes
            gutter={[
              { xl: 70, md: 80, xs: 60 },
              { xl: 0, md: 48, xs: 48 }
            ]}
          >
            {R.addIndex(R.map)(renderRewards, rewards)}
          </RewardTypes>
        </FlexCenter>
      </Section>
      <CorporateJoinUs onLinkClick={joinUsLinkClickCallback('page_bottom')} />
      {corporateCategory && (
        <Section>
          <SectionTitle>{t('corporate.home.corporatePartners')}</SectionTitle>
          <FlexCenter
            css={`
              margin: 32px 0 40px 0;
            `}
          >
            <Partners
              gutter={[
                { xl: 24, md: 24, xs: 8 },
                { xl: 24, md: 24, xs: 16 }
              ]}
            >
              {R.addIndex(R.map)(
                renderPartnerCorps,
                corporateCategory
                  ? R.prop('corporates', corporateCategory)
                  : Array(4).fill(null)
              )}
            </Partners>
          </FlexCenter>
          <BtnWrapper>
            <Link href='/corporate/listing' as='/corporate/listing'>
              <a onClick={handleGoToListingBtnClick}>
                <MoreBtn>
                  {t('corporate.home.understandCorporatePartner')}
                </MoreBtn>
              </a>
            </Link>
          </BtnWrapper>
        </Section>
      )}
      {corporates && (
        <Section
          css={`
            background-color: ${color.lightYellow};
          `}
        >
          <SectionTitle>{t('corporate.home.signedCorporates')}</SectionTitle>
          <FlexCenter
            css={`
              margin: 32px 0 40px 0;
            `}
          >
            <SignedCorps gutter={{ xl: 40, md: 24, xs: 16 }}>
              {R.addIndex(R.map)(
                renderSignedCorps,
                corporates || Array(6).fill(null)
              )}
            </SignedCorps>
          </FlexCenter>
          <BtnWrapper>
            <Link href='/corporate/listing' as='/corporate/listing'>
              <a onClick={handleGoToListingBtnClick}>
                <MoreBtn>
                  {t('corporate.home.moreParticipatingCorporates')}
                </MoreBtn>
              </a>
            </Link>
          </BtnWrapper>
        </Section>
      )}
    </Wrapper>
  )
}

Corporate.getInitialProps = async ({ store, req }) => {
  store.dispatch(getCorporateHomePageRequest(R.not(R.isNil(req))))
  return {}
}

Corporate.renderPersistentLayout = () => ({
  appviewNavigation: () => <AppviewNavigation />
})

export default Corporate
